"use client";

import type { ReactNode } from "react";
import { CopyButton } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  TABLE,
  TD,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  type CitationParts,
  partsToHtml,
  partsToPlain,
} from "@/lib/tool-kit/citation-parts";
import type { NumberFormatter } from "@/lib/tool-kit/stats-tests/format";

/*
 * Presentation shared by the `stats-tests` family (t-test, chi-square, ANOVA and the
 * rank, exact and normality tests). Like the tool kit, these own layout and never
 * vocabulary: every word arrives through props from the calling widget's strings.
 */

/**
 * A header cell without the kit's uppercase transform: statistics headers are symbols,
 * and uppercasing turns η into Η and p into P.
 */
export const STAT_TH =
  "border-border border-b px-3 py-2 font-medium text-foreground text-xs";

/** Significance levels offered by every test. Values are machine strings. */
export const ALPHA_VALUES = ["0.1", "0.05", "0.01", "0.001"] as const;

/** Confidence levels for the intervals. */
export const LEVEL_VALUES = ["0.9", "0.95", "0.99"] as const;

export function alphaOptions(fmt: NumberFormatter) {
  return ALPHA_VALUES.map((value) => ({ value, label: fmt(Number(value)) }));
}

export function levelOptions(fmt: NumberFormatter) {
  return LEVEL_VALUES.map((value) => ({
    value,
    label: fmt(Number(value), { style: "percent", maximumFractionDigits: 1 }),
  }));
}

/** Text with `italic` runs set in <em> — statistic symbols in an APA line. */
export function RichLine({ parts }: { parts: CitationParts }) {
  return (
    <>
      {parts.map((part, index) =>
        part.italic ? (
          <em key={`${index}-${part.text}`}>{part.text}</em>
        ) : (
          <span key={`${index}-${part.text}`}>{part.text}</span>
        )
      )}
    </>
  );
}

/** The APA-style line, with a copy button that keeps the italics in Word and Docs. */
export function ApaReport({
  heading,
  parts,
  note,
  copyLabel,
  copiedLabel,
}: {
  heading: string;
  parts: CitationParts;
  note?: ReactNode;
  copyLabel: string;
  copiedLabel: string;
}) {
  return (
    <div className={`${RESULT_CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{heading}</h3>
      <p className="break-words text-base text-foreground leading-relaxed">
        <RichLine parts={parts} />
      </p>
      {note ? <p className={HINT}>{note}</p> : null}
      <CopyButton
        copiedLabel={copiedLabel}
        html={partsToHtml(parts)}
        label={copyLabel}
        small
        text={partsToPlain(parts)}
      />
    </div>
  );
}

/** "How this was calculated": formulas with the reader's numbers substituted. */
export function Working({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4 sm:p-5">
      <h3 className={WIDGET_H3}>{heading}</h3>
      <div className="space-y-2 break-words font-mono text-muted-foreground text-sm leading-relaxed">
        {children}
      </div>
    </div>
  );
}

/** The verdict at α, in words, under the numbers. */
export function Decision({
  reject,
  children,
}: {
  reject: boolean;
  children: ReactNode;
}) {
  return (
    <p
      className={
        reject
          ? "rounded-lg border border-primary/40 bg-primary/5 p-4 text-foreground text-sm"
          : "rounded-lg border border-border bg-muted/40 p-4 text-foreground text-sm"
      }
    >
      {children}
    </p>
  );
}

/** Labels in the first column read as text; the numbers after them line up as code. */
function cellClass(index: number): string {
  return index === 0 ? TD : `${TD} font-mono`;
}

/** A small table whose cells may hold markup (italic symbols, emphasis). */
export function ResultTable({
  caption,
  head,
  rows,
}: {
  caption?: string;
  head: readonly ReactNode[];
  rows: readonly { key: string; cells: readonly ReactNode[] }[];
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        {caption ? (
          <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
            {caption}
          </caption>
        ) : null}
        <thead>
          <tr>
            {head.map((cell, index) => (
              // Column headers are positional; two may legitimately read the same.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional header cells
              <th className={STAT_TH} key={index} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              {row.cells.map((cell, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                <td className={cellClass(index)} key={index}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** "Try an example" — fills the form with the widget's sample data. */
export function ExampleButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button className={BUTTON_SECONDARY} onClick={onClick} type="button">
      {label}
    </button>
  );
}
